import styled, { css } from 'styled-components';

import { ThemeMode } from '@/helpers/enums';

import { device, hexToRGB } from '@/styles/helpers';

export interface ScTabNavigationProps {
  left: number;
  width: number;
  maxWidth100?: boolean;
  isShadowLeft: boolean;
  isShadowRight: boolean;
  padding: number;
  fullScreen?: boolean;
  withoutShadow?: boolean;
  withBorderMarker?: boolean;
  withUnderlineMarker?: boolean;
  mode?: ThemeMode;
}

interface ScCategoriesWrapperProps {
  padding?: number;
  paddingMob?: number;
  maxWidth100?: boolean;
  withUnderlineMarker?: boolean;
}

export type ScNavigationItemProps = Pick<
  ScTabNavigationProps,
  'withBorderMarker' | 'mode' | 'withUnderlineMarker'
> & {
  isSelected?: boolean;
  width?: string;
  navItemsCount: number;
};

export const ScCategoriesWrapper = styled.div<ScCategoriesWrapperProps>`
  width: 100%;

  & > .mob-full {
    width: 100%;
    position: relative;

    & > div {
      margin-bottom: 0;
    }

    @media (${device.mobile}) {
      ${({ maxWidth100, paddingMob, padding }) =>
        !maxWidth100 &&
        css`
          left: ${`-${padding}px`};
          width: calc(100vw + ${`${paddingMob}px`});
        `}
    }
  }

  ${({ withUnderlineMarker }) =>
    withUnderlineMarker &&
    css`
      @media (${device.mobile}) {
        position: relative;
        left: -16px;
        width: 100vw;
      }
    `}

  & .btn-left, .btn-right {
    position: absolute;
    z-index: 3;
    background: none;
    top: 50%;
    cursor: pointer;
    width: 24px;

    .arrow-icon {
      color: ${({ theme }) => theme.white}
    }

    @media (${device.mobile}) {
      display: none;
    }
  }

  & .btn-left {
    left: 0px;
    transform: translateY(calc(50% + 2px));
    rotate: 180deg;
  }

  & .btn-right {
    transform: translateY(calc(-50% + 2px));
    right: 0px;
  }
`;

export const ScTabNavigation = styled.div<ScTabNavigationProps>`
  overflow-x: auto;
  width: 100%;
  margin-bottom: 32px;
  min-height: 48px;
  padding-top: 4px;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }

  ${({ withoutShadow, isShadowLeft, isShadowRight }) =>
    !withoutShadow &&
    css`
      &:after {
        content: '';
        transform: ${
          isShadowLeft ? 'translate(0) scale(1.1)' : 'translate(-100%)'
        };
        transition: 0.3s;
        background: linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, #000000 100%);
        position: absolute;
        left: -1px;
        top: 0;
        bottom: 0;
        width: ${isShadowLeft ? '56px' : 0};
        z-index: 1;
      }

      &:before {
        content: '';
        transform: ${
          isShadowRight ? 'translate(0) scale(1.1)' : 'translate(100%)'
        };
        transition: 0.3s;
        background: linear-gradient(270deg, #000000 0%, rgba(0, 0, 0, 0) 100%);
        position: absolute;
        right: -1px;
        top: 0;
        bottom: 0;
        width: ${isShadowRight ? '56px' : 0};
        z-index: 1;
      }
    `}

  @media (${device.mobile}) {
    position: relative;
    padding-left: ${({ padding }) => `${padding}px`};
    padding-top: 4px;
    &::before,
    &::after {
      display: none;
    }
    ${({ fullScreen, maxWidth100 }) =>
      !fullScreen &&
      maxWidth100 &&
      css`
        padding: 4px 0 0;
        width: 100%;
        margin-left: 0;
      `}
  }

  & > div {
    margin: 0 auto;
    position: relative;
    scroll-snap-align: center;
    min-width: ${({ maxWidth100 }) => (maxWidth100 ? '100%' : '900px')};
    width: max-content;
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    background-color: ${({ theme }) => theme.grayLightColor};
    border-radius: 24px;
    height: 40px;

    @media (${device.mobile}) {
      ${({ maxWidth100, padding }) =>
        maxWidth100
          ? css`
              min-width: 100%;
            `
          : css`
              min-width: max-content;
              &:after {
                content: '';
                display: block;
                position: absolute;
                right: ${`-${padding - 18}px`};
                width: ${`${padding - 18}px`};
                height: 1px;
              }
            `}

      ${({ withUnderlineMarker }) =>
        withUnderlineMarker &&
        css`
          padding: 0 16px;
        `}
    }

    @media (${device.mobileS}) {
      ${({ maxWidth100, padding }) =>
        !(maxWidth100 || padding) &&
        css`
          margin: 0 18px;
        `}
    }

    & > .marker {
      position: absolute;
      height: calc(100% + 8px);
      z-index: 10;
      top: -4px;
      left: ${({ left }) => (left ? `${left}px` : '0')};
      width: ${({ width }) => (width ? `${width}px` : '0')};
      background-color: ${({ theme }) => theme.whiteColor};
      border-radius: 24px;
      transition: 0.5s ease;
      z-index: 0;
    }
  }

  ${({ withBorderMarker, mode }) =>
    withBorderMarker &&
    css`
      min-height: 40px;
      margin-bottom: 0;
      padding-top: 0;
      overflow: visible;

      & > div {
        background-color: ${({ theme }) =>
          mode === ThemeMode.Dark ? theme.grayLightColor : theme.gray1Color};

        & > .marker {
          height: calc(100% + 4px);
          top: -2px;
          border: ${({ theme }) =>
            `2px solid ${
              mode === ThemeMode.Dark ? theme.whiteColor : theme.blackColor
            }`};
          background-color: transparent;
        }
      }

      @media (${device.mobile}) {
        padding-top: 0;
      }
    `}

  ${({ withUnderlineMarker, mode }) =>
    withUnderlineMarker &&
    css`
      min-height: 32px;
      margin-bottom: 0;
      padding-top: 0;
      overflow-x: auto;

      & > div {
        background-color: transparent;
        height: 32px;

        & > div:not(.marker) {
          border-bottom: 2px solid
            ${({ theme }) =>
              mode === ThemeMode.Dark
                ? hexToRGB(theme.whiteColor, 0.2)
                : hexToRGB(theme.blackColor, 0.2)};
        }

        & > .marker {
          height: 2px;
          top: auto;
          bottom: 0;
          background-color: ${({ theme }) =>
            mode === ThemeMode.Dark ? theme.whiteColor : theme.blackColor};
          z-index: 2;
        }
      }

      @media (${device.mobile}) {
        padding-top: 0;
        scroll-snap-type: x mandatory;
        scroll-padding: 0 16px;
      }
    `}
`;

export const ScNavigationItem = styled.div<ScNavigationItemProps>`
  width: ${({ width }) => (width ? width : 'max-content')};
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1.2em;
  color: ${({ theme, isSelected }) =>
    isSelected ? theme.blackColor : theme.blackLightColor};
  font-weight: bold;
  font-size: ${({ withUnderlineMarker }) =>
    withUnderlineMarker ? '14px' : '16px'};
  text-transform: uppercase;
  transition: color 0.3s ease-in;
  cursor: pointer;

  & > a {
    color: inherit;
  }

  ${({ withBorderMarker, withUnderlineMarker, isSelected, mode }) =>
    (withBorderMarker || withUnderlineMarker) &&
    mode === ThemeMode.Dark &&
    css`
      color: ${({ theme }) =>
        isSelected ? theme.whiteColor : theme.blackLightColor};

      & > a {
        color: ${({ theme }) =>
          isSelected ? theme.whiteColor : theme.blackLightColor};
      }
    `}

  & > a,
  & > span {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    z-index: 2;

    & > .icon {
      display: inline-block;
      font-size: 20px;
      min-width: 20px;
      margin-left: 4px;
    }

    & > svg,
    & > img {
      transition: 0.3s;
      height: 24px;
      width: 24px;
      object-fit: contain;
    }
  }

  ${({ withUnderlineMarker, navItemsCount, width }) =>
    withUnderlineMarker &&
    css`
      @media (${device.mobile}) {
        scroll-snap-align: end;
        width: ${navItemsCount > 2 ? 'auto' : width};
      }
    `}

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: ${({ theme, isSelected, mode }) =>
        mode === ThemeMode.Dark
          ? isSelected
            ? theme.blackColor
            : theme.whiteColor
          : theme.blackColor};

      & > a {
        color: inherit;
      }
      svg,
      img {
        transform: scale(1.17);
      }
    }
  }
`;
